/**
 * Enhanced fetch with timeout handling
 * @param {string} url - The URL to fetch
 * @param {Object} options - Fetch options
 * @param {number} timeout - Timeout in milliseconds
 * @returns {Promise<Response>} - The fetch response
 */
export const fetchWithTimeout = async (url, options, timeout = 10000) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  
  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('Request timed out');
    }
    throw error;
  }
};

/**
 * Handle API errors with special handling for Cloudflare-specific issues
 * @param {Error} error - The error object
 * @param {string} fallbackMessage - Fallback error message
 * @returns {string} - Formatted error message
 */
export const handleApiError = (error, fallbackMessage) => {
  // Check for Cloudflare-specific errors
  if (error.message && error.message.includes('1015')) {
    // Cloudflare rate limiting error
    return 'Too many requests. Please try again later.';
  }
  
  if (error.message && error.message.includes('timeout')) {
    return 'The request timed out. Please check your connection and try again.';
  }
  
  // Default error handling
  return error.message || fallbackMessage;
};

/**
 * Validate email format
 * @param {string} email - Email to validate
 * @returns {boolean} - True if valid
 */
export const validateEmail = (email) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(String(email).toLowerCase());
};
